import { createContext, ReactNode, startTransition, useContext, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { type TabKey, DEFAULT_TAB } from '../../_consts/constants';

import { sellerQueries } from '@/api';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import { MembershipOrderPreviewTutorialProvider } from '@/components/membership/MembershipOrderPreviewTutorial';
import { useAppSelector } from '@/redux/store';
import { OrderItem } from '@/types/order-list';
import { CarrierState } from '@/types/shipping';
import { ShippingCompany } from '@/types/user-info';
import { useQuery } from '@tanstack/react-query';
import { type MarketStoreListItem } from '../../_consts/constants';
import { membershipOrderDetailsMock } from './membership-order-details-mock';

interface OrderDetailsContextType {
  // 유저가 갖고 있는 마켓 리스트
  currentTab: TabKey;
  setCurrentTab: (tab: TabKey) => void;
  selectedMarket: { storeId: string; marketId: string } | null;
  setSelectedMarket: (market: { storeId: string; marketId: string }) => void;
  // 날짜 관련 상태와 함수
  startDate: Date | undefined;
  endDate: Date | undefined;
  handleStartDate: (date: Date | undefined) => void;
  handleEndDate: (date: Date | undefined) => void;
  // 검색 관련 상태와 함수
  searchField: string;
  searchValue: string;
  setSearchField: (field: string) => void;
  setSearchValue: (value: string) => void;
  handleSearch: () => void;
  resetSearch: () => void;
  isSearchActive: boolean; // 내부용

  checkedOrders: OrderItem[]; // ✅ Order 배열로 변경
  setCheckedOrders: React.Dispatch<React.SetStateAction<OrderItem[]>>; // ✅ 타입 수정

  shippingInfo: Record<string, { carrier: string; trackingNumber: string }>; // 배송 정보 상태
  setShippingInfo: React.Dispatch<
    React.SetStateAction<Record<string, { carrier: string; trackingNumber: string }>>
  >; // 배송 정보 설정 함수
  shippingCompanyData: ShippingCompany[]; // 배송사 데이터
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  setPagination: React.Dispatch<
    React.SetStateAction<{
      pageIndex: number;
      pageSize: number;
    }>
  >;
  carrierState: {
    target: OrderItem;
    carrier: ShippingCompany;
    trackingNumber: string;
  }[];
  setCarrierState: React.Dispatch<React.SetStateAction<CarrierState[]>>;
  errorCarrierState: CarrierState[];
  setErrorCarrierState: React.Dispatch<React.SetStateAction<CarrierState[]>>;
}

const OrderDetailsContext = createContext<OrderDetailsContextType | undefined>(undefined);

// 마켓 리스트 패칭
export type RegisteredMarketResponse = {
  data: Array<{
    RegisteredMarketList: MarketStoreListItem[];
  }>;
  message: string;
};

export const OrderDetailsProvider = ({ children }: { children: ReactNode }) => {
  const user = useAppSelector((state) => state.user);
  const isPreviewMode = useMembershipPreviewMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = (searchParams.get('orderTab') as TabKey) || DEFAULT_TAB;
  const selectedMarketFromUrl = searchParams.get('select');
  const selectedMarket = selectedMarketFromUrl
    ? {
        storeId: selectedMarketFromUrl.split('_')[0],
        marketId: selectedMarketFromUrl.split('_')[1],
      }
    : null;
  const [checkedOrders, setCheckedOrders] = useState<OrderItem[]>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [shippingInfo, setShippingInfo] = useState<
    Record<string, { carrier: string; trackingNumber: string }>
  >({});

  const [currentTab, setCurrentTab] = useState<TabKey>(tabFromUrl);

  const getUserInfoQuery = useQuery({
    ...sellerQueries.getInfo({ cplatToken: user.CplatToken }),
    enabled: !isPreviewMode && Boolean(user.CplatToken),
  });

  const shippingCompanyData = isPreviewMode
    ? membershipOrderDetailsMock.shippingCompanyList
    : getUserInfoQuery.data?.data[0].shippingCompanyList ?? [];

  const setSelectedMarket = (marketInfo: {
    storeId: string | number;
    marketId: string | number;
  }) => {
    const newParams = new URLSearchParams();
    newParams.set('select', `${marketInfo.storeId}_${marketInfo.marketId}`);
    setSearchParams(newParams);
  };

  // useEffect(() => {
  //   // 상태 → URL 동기화
  //   if (isSyncingFromUrl.current) {
  //     isSyncingFromUrl.current = false;
  //     return; // URL이 바뀌어서 상태를 맞춘 직후에는 쿼리 세팅을 스킵
  //   }

  //   // TODO: 수정 필요.....
  //   // const isScrapping = selectedMarket?.type === 'scrapping';
  //   // const isScrapping = false;

  //   const newParams = new URLSearchParams();
  //   newParams.set('orderTab', currentTab);
  //   // if (isScrapping && selectedMarket.marketAlias) {
  //   //   newParams.set('MarketAlias_Scrapping', selectedMarket.marketAlias);
  //   // }

  //   // ... (변경사항 있을 때만 setSearchParams)
  //   let isChanged = false;
  //   for (const [key, value] of newParams.entries()) {
  //     if (searchParams.get(key) !== value) {
  //       isChanged = true;
  //       break;
  //     }
  //   }
  //   for (const [key] of searchParams.entries()) {
  //     if (!newParams.has(key)) {
  //       isChanged = true;
  //       break;
  //     }
  //   }
  //   if (isChanged) {
  //     setSearchParams(newParams, { replace: false }); // 또는 setSearchParams(newParams); 기본값이 push
  //   }
  //   // eslint-disable-next-line
  // }, [currentTab, selectedMarket]);

  //  날짜 상태 추가
  const [startDate, setStartDate] = useState<Date | undefined>(() => {
    if (isPreviewMode) return membershipOrderDetailsMock.startDate;

    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 6);
    return sevenDaysAgo;
  });

  const [endDate, setEndDate] = useState<Date | undefined>(() =>
    isPreviewMode ? membershipOrderDetailsMock.endDate : new Date(),
  );

  // 날짜 핸들러 추가
  const handleStartDate = (date: Date | undefined) => {
    if (date && endDate && date > endDate) {
      startTransition(() => {
        setStartDate(date);
        setEndDate(date);
      });
    } else {
      startTransition(() => {
        setStartDate(date);
      });
    }
  };

  const handleEndDate = (date: Date | undefined) => {
    if (date && startDate && date < startDate) {
      startTransition(() => {
        setStartDate(date);
        setEndDate(date);
      });
    } else {
      startTransition(() => {
        setEndDate(date);
      });
    }
  };

  // ▼▼▼ 검색 상태 추가 ▼▼▼
  const [searchField, setSearchField] = useState<string>('ExternalProductInfo'); // 기본: 상품명
  const [searchValue, setSearchValue] = useState<string>('');
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);

  // ▼▼▼ 검색 핸들러 ▼▼▼
  const handleSearch = () => {
    setIsSearchActive(true);
  };

  const resetSearch = () => {
    setSearchValue('');
    setIsSearchActive(false);
  };

  const [carrierState, setCarrierState] = useState<CarrierState[]>(() =>
    isPreviewMode ? membershipOrderDetailsMock.readyToShipCarrierState : [],
  ); // 배송사 상태 배열로 변경
  const [errorCarrierState, setErrorCarrierState] = useState<CarrierState[]>([]); // 에러 상태 배열
  return (
    <OrderDetailsContext.Provider
      value={{
        currentTab,
        setCurrentTab,
        //API 실제 데이터 호출
        selectedMarket,
        setSelectedMarket,

        startDate,
        endDate,
        handleStartDate,
        handleEndDate,

        // ▼▼▼ 검색 관련 값 추가 ▼▼▼
        searchField,
        searchValue,
        setSearchField,
        setSearchValue,
        handleSearch,
        resetSearch,
        isSearchActive, // 내부용

        checkedOrders, // 선택된 주문 번호들
        setCheckedOrders, // 선택된 주문 번호 설정 함수

        shippingCompanyData, // 배송사 데이터
        shippingInfo, // 배송 정보 상태
        setShippingInfo, // 배송 정보 설정 함수

        pagination, // 페이지네이션 상태
        setPagination, // 페이지네이션 설정 함수

        carrierState, // 배송사 상태 배열
        setCarrierState, // 배송사 상태 설정 함수

        errorCarrierState, // 에러 상태 배열
        setErrorCarrierState, // 에러 상태 설정 함수
      }}
    >
      <MembershipOrderPreviewTutorialProvider>{children}</MembershipOrderPreviewTutorialProvider>
    </OrderDetailsContext.Provider>
  );
};

export const useOrderDetails = () => {
  const context = useContext(OrderDetailsContext);
  if (!context) {
    throw new Error('useOrderDetails must be used within an OrderDetailsProvider');
  }
  return context;
};
